import React, { useState, useEffect } from 'react';
import Loader from '../../components/Common/Loader';
import Collapse from '../../components/Common/Collapse';
import LockedModal from '../../components/Student/LockedModal';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { getHomeworks } from '../../services/studentService';
import SegToggle from '../../components/Common/SegToggle';

const TABS = [
  { key: 'all',    label: 'Все задания' },
  { key: 'active', label: 'Активные' },
  { key: 'done',   label: 'Выполненные' },
];

const MonthRu = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];

// Есть ли непустой черновик ответов в localStorage (набрано, но ещё не сдано).
const hasLocalDraft = (lessonId) => {
  try {
    const dr = JSON.parse(localStorage.getItem(`hwdraft:${lessonId}`) || '{}');
    return Object.values(dr).some(v => v && String(v).trim() && String(v).trim() !== '[]');
  } catch (e) { return false; }
};

const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

const IcSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
// Иконка модуля = иконка раздела «Домашние задания» из сайдбара (карандаш)
const IcSection = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const IcChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IcArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IcEye = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
);
const IcStar = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
);

const IcLockSm = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10.5" width="16" height="10.5" rx="2.4"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>);

// Одна карточка = всё ДЗ урока. Клик открывает полное ДЗ.
function HwRow({ group, navigate }) {
  const [lock, setLock] = useState(false);
  const { lesson, lessonId, locked, completed, total, correct, answeredCount, answered, extra, retry } = group;
  const d = lesson?.date ? new Date(lesson.date) : null;
  const pct = total ? Math.round((correct / total) * 100) : 0;
  const donePct = total ? Math.round((answeredCount / total) * 100) : 0;
  // Состояние бейджа: done — зелёный; started — фиолетовый; todo — янтарный.
  const state = completed ? 'done' : (answered ? 'started' : 'todo');
  const open = () => { if (locked) return setLock(true); navigate(`/homework/${lessonId}`, { state: { from: '/homework' } }); };
  const btnText = completed ? (retry ? 'Продолжить' : 'Посмотреть') : (state === 'started' ? 'Продолжить' : 'Выполнить');
  return (
    <div className={`hwc ${state}${locked ? ' locked' : ''}`} onClick={open}>
      <div className="hwc-date" data-state={state}>
        {d ? (
          <><b>{d.getDate()}</b><i>{MonthRu[d.getMonth()]}</i></>
        ) : <IcStar />}
      </div>

      <div className="hwc-main">
        <div className="hwc-title">{lesson?.title || 'Урок'}</div>
        <div className="hwc-sub">{total} {plural(total, ['номер', 'номера', 'номеров'])}</div>
        {/* Куратор выдал ещё попытку: ДЗ остаётся сданным, засчитается лучший результат */}
        {completed && (retry || extra > 0) && (
          <div className="s-attempt">{retry ? 'Идёт повторная попытка' : 'Доступна ещё одна попытка'}</div>
        )}
      </div>

      <div className="hwc-prog">
        {completed ? (
          <>
            <span className="hwc-done-pct">Выполнено на {pct}%</span>
            <span className="hwc-cnt">{correct} из {total}</span>
          </>
        ) : (
          <>
            <span className="hwc-cnt"><b>{answeredCount} / {total}</b> выполнено</span>
            <span className="hwc-bar"><span style={{ width: `${donePct}%` }} /></span>
          </>
        )}
      </div>

      {locked
        ? <span className="lock-chip" title="Откроется с полным курсом"><IcLockSm /></span>
        : (
          <button className={`hwc-btn${completed && !retry ? ' view' : ''}`} onClick={e => { e.stopPropagation(); open(); }}>
            {btnText} {completed && !retry ? <IcEye /> : <IcArrow />}
          </button>
        )}
      <LockedModal open={lock} onClose={() => setLock(false)} kind="homework" title={lesson?.title} />
    </div>
  );
}

export default function StudentHomeworkPage() {
  const location = useLocation();
  const [homeworks, setHomeworks] = useState([]);
  const [loading, setLoading]     = useState(true);
  // Вкладка может прийти с «Главной» (клик по «Активные задания») через state.
  const [tab, setTab]             = useState(location.state?.tab || 'all');
  const [search, setSearch]       = useState('');
  const [open, setOpen]           = useState({}); // { [moduleKey]: bool }
  const navigate = useNavigate();
  const { selectedCourseId } = useOutletContext() || {};

  useEffect(() => {
    getHomeworks(selectedCourseId)
      .then(res => setHomeworks(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedCourseId]);

  // Группируем по уроку: одно ДЗ = один урок. Завершённость — по уроку целиком.
  // Балл = число верных заданий (answer.status === 'correct') из всех заданий урока.
  const groupsMap = {};
  const groups = [];
  homeworks.forEach(hw => {
    const lid = hw.lessonId || hw.Lesson?.id || 'unknown';
    if (!groupsMap[lid]) {
      groupsMap[lid] = { lessonId: lid, lesson: hw.Lesson, locked: !!hw.locked, completed: false, total: 0, correct: 0, answeredCount: 0, answered: false, extra: 0, retry: false };
      groups.push(groupsMap[lid]);
    }
    const g = groupsMap[lid];
    g.total += 1;
    if (hw.answer?.status === 'correct') g.correct += 1;
    if (hw.answer) { g.answered = true; g.answeredCount += 1; } // есть ответ → «начато»
    if (hw.lessonCompleted) g.completed = true;
    if (hw.lessonExtraAttempts) g.extra = hw.lessonExtraAttempts;
    if (hw.lessonRetryActive) g.retry = true;
  });
  // «Начато» также если есть локальный черновик (набрано, но не проверено).
  groups.forEach(g => { if (!g.completed && !g.answered && hasLocalDraft(g.lessonId)) g.answered = true; });

  // Поиск по названию урока или раздела.
  const q = search.trim().toLowerCase();
  const filtered = groups.filter(g =>
    !q || (g.lesson?.title || '').toLowerCase().includes(q) || (g.lesson?.Section?.name || '').toLowerCase().includes(q)
  );

  const activeGroups = filtered.filter(g => !g.completed);
  const doneGroups   = filtered.filter(g => g.completed);
  const counts = { all: filtered.length, active: activeGroups.length, done: doneGroups.length };
  const shown = tab === 'active' ? activeGroups : (tab === 'done' ? doneGroups : filtered);

  // Группируем уроки текущего таба по разделу-модулю.
  const modMap = {};
  const modules = [];
  shown.forEach(g => {
    const sec = g.lesson?.Section;
    const key = sec?.id ?? sec?.name ?? 'no-section';
    if (!modMap[key]) {
      modMap[key] = {
        key,
        name: sec?.name || 'Без раздела',
        order: sec?.sortOrder ?? sec?.sort_order ?? 9999,
        items: [],
      };
      modules.push(modMap[key]);
    }
    modMap[key].items.push(g);
  });
  modules.sort((a, b) => a.order - b.order);
  // Внутри модуля — порядок как в админке (sortOrder урока).
  modules.forEach(m => m.items.sort((a, b) =>
    (a.lesson?.sortOrder ?? a.lesson?.sort_order ?? 0) - (b.lesson?.sortOrder ?? b.lesson?.sort_order ?? 0)
  ));

  // По умолчанию все модули закрыты — раскрываются только по клику.
  const isOpen = (k) => !!open[k];
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  if (loading) return <div className="s-page"><Loader /></div>;

  return (
    <div className="s-page">
      <div className="s-page-header">
        <div>
          <h1 className="s-page-title">Задания</h1>
          <p className="s-page-sub">Домашние задания по модулям</p>
        </div>
      </div>

      {/* Поиск слева + тумблер справа — одной строкой */}
      <div className="s-filter-row">
        <div className="pg-search">
          <IcSearch />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Поиск задания..." />
        </div>
        <SegToggle
          className="glass"
          value={tab}
          onChange={setTab}
          options={TABS.map(t => ({ key: t.key, label: t.label, count: counts[t.key] > 0 ? counts[t.key] : null }))}
        />
      </div>

      {modules.length === 0 ? (
        <div className="pg-empty">{q ? 'Ничего не найдено' : 'Нет заданий в этой категории'}</div>
      ) : (
        modules.map(mod => {
          const opened = (q || tab !== 'all') ? true : isOpen(mod.key);
          const n = mod.items.length;
          const doneN = mod.items.filter(g => g.completed).length;
          const pct = n ? Math.round((doneN / n) * 100) : 0;
          return (
            <div key={mod.key} className={`mod${opened ? ' open' : ''}`}>
              <div className="mod-head" onClick={() => toggle(mod.key)}>
                <div className="mod-ic"><IcSection /></div>
                <div className="mod-info">
                  <div className="mod-title">{mod.name}</div>
                  <div className="mod-sub">{n} {plural(n, ['задание', 'задания', 'заданий'])}</div>
                </div>
                <div className="mod-prog">
                  <div className="mod-prog-top"><span>Выполнено {doneN} / {n}</span><b>{pct}%</b></div>
                  <div className="mod-prog-bar"><span style={{ width: `${pct}%` }} /></div>
                </div>
                <span className="mod-count">{n}</span>
                <span className="mod-chev"><IcChevron /></span>
              </div>

              <Collapse open={opened} keepMounted>
                <div className="mod-body">
                  <div className="hw-list">
                    {mod.items.map(group => (
                      <HwRow key={group.lessonId} group={group} navigate={navigate} />
                    ))}
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })
      )}
    </div>
  );
}
